import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../AuthContext";

// Shared cache + listeners so Header + user menu badge are in sync.
const _state = { count: 0, loaded: false };
const _listeners = new Set();
let _poller = null;

function _notify() { _listeners.forEach((fn) => fn()); }

async function fetchUnseen() {
  try {
    const res = await api.get("/me/matches/unseen-count");
    _state.count = res.data?.count || 0;
    _state.loaded = true;
    _notify();
  } catch {
    _state.loaded = true;
  }
}

export async function markMatchesSeen() {
  try {
    await api.post("/me/matches/mark-seen");
    _state.count = 0;
    _notify();
  } catch { /* ignore */ }
}

export function useMatchCount() {
  const { user } = useAuth();
  const location = useLocation();
  const [, force] = useState(0);

  const refresh = useCallback(() => { fetchUnseen(); }, []);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    return () => _listeners.delete(fn);
  }, []);

  // Poll + refresh on route change when signed in
  useEffect(() => {
    if (!user) {
      _state.count = 0;
      _state.loaded = false;
      _notify();
      if (_poller) { clearInterval(_poller); _poller = null; }
      return;
    }
    fetchUnseen();
    if (_poller) clearInterval(_poller);
    _poller = setInterval(fetchUnseen, 60000);
    return () => { if (_poller) { clearInterval(_poller); _poller = null; } };
  }, [user]);

  useEffect(() => {
    if (user) fetchUnseen();
  }, [location.pathname, user]);

  return { count: _state.count, loaded: _state.loaded, refresh };
}

export const MatchBadge = ({ className = "", testId = "match-count-badge" }) => {
  const { count } = useMatchCount();
  if (!count) return null;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold leading-none ${className}`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};
